import type { CSSProperties, ReactNode } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { EmptyState } from '@/components/ui/EmptyState'
import {
  isStickyId,
  stickyCellClass,
  stickyOffsets,
  stickyRightOffsets,
} from '@/components/table/stickyColumns'

export type SortDir = 'asc' | 'desc'

export type DataTableColumn<T> = {
  id: string
  header: ReactNode
  width?: string
  sortable?: boolean
  sortKey?: string
  align?: 'left' | 'center' | 'right'
  cell: (row: T) => ReactNode
}

type DataTableProps<T> = {
  columns: DataTableColumn<T>[]
  rows: T[]
  rowKey: (row: T) => string
  loading?: boolean
  emptyTitle?: string
  emptyDescription?: string
  sortBy?: string
  sortDir?: SortDir
  onSortChange?: (sortBy: string, sortDir: SortDir) => void
  onRowClick?: (row: T) => void
  headerGroups?: ReactNode
  stickyLeftColumnIds?: string[]
  stickyRightColumnIds?: string[]
  showRowNumbers?: boolean
  rowNumberOffset?: number
}

function parseWidthPx(width?: string): number | undefined {
  if (!width) return undefined
  const m = width.trim().match(/^(\d+)px$/i)
  return m ? Number(m[1]) : undefined
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  emptyTitle = 'Không có dữ liệu',
  emptyDescription,
  sortBy,
  sortDir,
  onSortChange,
  onRowClick,
  headerGroups,
  stickyLeftColumnIds = [],
  stickyRightColumnIds = [],
  showRowNumbers = false,
  rowNumberOffset = 0,
}: DataTableProps<T>) {
  function handleSort(col: DataTableColumn<T>) {
    if (!col.sortable || !col.sortKey || !onSortChange) return
    if (sortBy === col.sortKey) {
      onSortChange(col.sortKey, sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      onSortChange(col.sortKey, 'asc')
    }
  }

  const widthMap: Record<string, number | undefined> = {}
  for (const c of columns) widthMap[c.id] = parseWidthPx(c.width)

  const leftOff = stickyOffsets(stickyLeftColumnIds, widthMap)
  const rightOff = stickyRightOffsets(stickyRightColumnIds, widthMap)

  function cellStyle(col: DataTableColumn<T>): CSSProperties {
    const side = isStickyId(col.id, stickyLeftColumnIds, stickyRightColumnIds)
    const style: CSSProperties = {}
    if (col.width) style.width = col.width
    if (side === 'left') style.left = (leftOff[col.id] ?? 0) + (showRowNumbers ? 40 : 0)
    if (side === 'right') style.right = rightOff[col.id] ?? 0
    return style
  }

  return (
    <div className="max-w-full overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-level-1)]">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1080px] border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50 text-left text-xs font-semibold tracking-wide text-slate-500 uppercase">
            {headerGroups}
            <tr className="border-b border-[var(--color-border)]">
              {showRowNumbers ? <th className="sticky left-0 z-30 w-10 min-w-10 bg-slate-50 px-2 py-2 text-center">STT</th> : null}
              {columns.map((col) => {
                const align =
                  col.align === 'center'
                    ? 'text-center'
                    : col.align === 'right'
                      ? 'text-right'
                      : 'text-left'
                const side = isStickyId(
                  col.id,
                  stickyLeftColumnIds,
                  stickyRightColumnIds,
                )
                return (
                  <th
                    key={col.id}
                    className={`px-3 py-2 ${align} ${stickyCellClass({ side, isHeader: true })}`}
                    style={cellStyle(col)}
                  >
                    {col.sortable && col.sortKey ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 uppercase hover:text-[var(--color-text)]"
                        onClick={() => handleSort(col)}
                      >
                        {col.header}
                        {sortBy === col.sortKey ? (
                          sortDir === 'asc' ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : (
                            <ArrowDown className="h-3 w-3" />
                          )
                        ) : (
                          <ArrowUpDown className="h-3 w-3 opacity-40" />
                        )}
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + (showRowNumbers ? 1 : 0)} className="px-3 py-10 text-center text-slate-500">
                  Đang tải...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (showRowNumbers ? 1 : 0)}>
                  <EmptyState title={emptyTitle} description={emptyDescription} />
                </td>
              </tr>
            ) : (
              rows.map((row, rowIndex) => (
                <tr
                  key={rowKey(row)}
                  className={`group border-b border-slate-100 last:border-0 ${
                    onRowClick
                      ? 'cursor-pointer hover:bg-[var(--color-accent-soft)]/50'
                      : ''
                  }`}
                  style={{ height: 'var(--table-row-h)' }}
                  onClick={() => onRowClick?.(row)}
                >
                  {showRowNumbers ? <td className="sticky left-0 z-20 w-10 min-w-10 bg-[var(--color-surface)] px-2 py-1.5 text-center tabular-nums text-slate-500">{rowNumberOffset + rowIndex + 1}</td> : null}
                  {columns.map((col) => {
                    const align =
                      col.align === 'center'
                        ? 'text-center'
                        : col.align === 'right'
                          ? 'text-right'
                          : 'text-left'
                    const side = isStickyId(
                      col.id,
                      stickyLeftColumnIds,
                      stickyRightColumnIds,
                    )
                    return (
                      <td
                        key={col.id}
                        className={`px-3 py-1.5 align-middle ${align} ${stickyCellClass({
                          side,
                          isHeader: false,
                        })}`}
                        style={cellStyle(col)}
                      >
                        {col.cell(row)}
                      </td>
                    )
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
